import { apiJson } from '@/lib/http';

export interface MemoryExplorerDto {
  id: string;
  snippet: string;
  scope: {
    type: string;
    id: string;
  };
  timestamp: string;
  ttl: string | null;
  environment: string;
  deleted: boolean;
  relevanceScore?: number;
  deletedAt?: string;
  deletedBy?: string;
  recoveryDeadline?: string;
  projectId?: string;
  tags?: string[];
  categories?: string[];
  metadata?: Record<string, unknown>;
}

export interface MemoryExplorerRequest {
  project: string;
  search: string;
  scopes: string[];
  environment: string;
  dateFrom: string;
  dateTo: string;
  sortBy: string;
  sortDir: string;
  ttl: string;
  page: number;
  pageSize: number;
  includeDeleted: boolean;
  memoryType?: string;
}

export interface MemoryExplorerResponse {
  memories: MemoryExplorerDto[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface MemoryActionRequest {
  memoryId: string;
}

// Add GraphSearchResult interface if not present
export interface GraphSearchResult {
  source: string;
  relationship: string;
  destination: string;
  score: number;
  source_type?: string;
  destination_type?: string;
}

// Attributes (Topics) DTOs
export interface SubTopicItemDto {
  name: string;
  value: string;
}

export interface TopicItemDto {
  name: string;
  subTopics: SubTopicItemDto[];
}

export interface TopicsDto {
  topics: TopicItemDto[];
  users: string[];
}

// Delegate API calls to shared helper
const apiCall = <T = unknown>(endpoint: string, options: RequestInit = {}) => apiJson<T>(endpoint, options);

export const memoryExplorerApi = {
  async getMemories(request: MemoryExplorerRequest): Promise<MemoryExplorerResponse> {
    return apiCall('/memoryfrontend/explorer', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  async getAvailableProjects(): Promise<string[]> {
    return apiCall('/memoryfrontend/projects', {
      method: 'GET',
    });
  },

  async softDeleteMemory(memoryId: string): Promise<{ message: string; success: boolean }> {
    return apiCall('/memoryfrontend/soft-delete', {
      method: 'POST',
      body: JSON.stringify({ memoryId }),
    });
  },

  async restoreMemory(memoryId: string): Promise<{ message: string; success: boolean }> {
    return apiCall('/memoryfrontend/restore', {
      method: 'POST',
      body: JSON.stringify({ memoryId }),
    });
  },

  async permanentDeleteMemory(memoryId: string): Promise<{ message: string; success: boolean }> {
    return apiCall('/memoryfrontend/permanent-delete', {
      method: 'POST',
      body: JSON.stringify({ memoryId }),
    });
  },

  async getGraphRelationships(userId: string, projectId?: string, limit: number = 100): Promise<GraphSearchResult[]> {
    const params = new URLSearchParams({ userId, limit: limit.toString() });
    if (projectId) params.append('projectId', projectId);
    return apiCall(`/memoryfrontend/graph-relationships?${params.toString()}`, {
      method: 'GET',
    });
  },

  async getGraphUserIds(): Promise<string[]> {
    return apiCall('/memoryfrontend/graph-users', {
      method: 'GET',
    });
  },

  async getExplorerTopics(userId?: string, projectId?: string): Promise<TopicsDto> {
    const params = new URLSearchParams();
    if (userId) params.append('userId', userId);
    if (projectId) params.append('projectId', projectId);
    return apiCall(`/memoryfrontend/explorer-topics?${params.toString()}`, {
      method: 'POST',
    });
  },
}; 